import { Adventure, Items, MarkerDot, SavingItem } from "./types/types";

const url_prefix = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
const SUBSCRIBER_ID_HEADER = 'X-QQ-SSE-Subscriber-Id';

let sseSubscriberId: number | undefined;

const typedFetch = async <T>(input: string | URL | globalThis.Request, init?: RequestInit) => {
  let requestInit = init;
  const requestMethod = (init?.method || 'GET').toUpperCase();
  if (requestMethod !== 'GET' && requestMethod !== 'HEAD' && sseSubscriberId !== undefined) {
    const headers = new Headers(init?.headers);
    headers.set(SUBSCRIBER_ID_HEADER, sseSubscriberId.toString());
    requestInit = { ...init, headers };
  }

  const response = await fetch(input, requestInit);
  return {
    ok: response.ok,
    status: response.status,
    json: async () => (await response.json() as T)
  };
};

const backend = {
  createAdventure: async (name: string, rpg: string) => {
    return await typedFetch<{ id: string }>(`${url_prefix}/adventure`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, rpg })
      });
  },

  loadAdventure: async (adventureId: string) => {
    return await typedFetch<Adventure>(`${url_prefix}/adventure/${adventureId}`);
  },

  loadItems: async (adventureId: string) => {
    return await typedFetch<Items>(`${url_prefix}/adventure/${adventureId}/items`);
  },

  insertItem: async (adventureId: string, item: SavingItem) => {
    return await typedFetch<{ id: string }>(`${url_prefix}/adventure/${adventureId}/items`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item)
      });
  },

  updateItem: async (adventureId: string, item: SavingItem) => {
    return await typedFetch<{ id: string }>(`${url_prefix}/adventure/${adventureId}/items/${item.id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item)
      });
  },

  deleteItem: async (adventureId: string, itemId: string) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}`,
      {
        method: 'DELETE'
      });
  },

  archiveItem: async (adventureId: string, itemId: string, value: boolean) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/archived`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ archived: value })
      });
  },

  starItem: async (adventureId: string, itemId: string, value: boolean) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/starred`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ starred: value })
      });
  },

  uploadImage: async (adventureId: string, itemId: string, file: File) => {
    const formData = new FormData();
    formData.append("image", file as Blob, file.name);
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/image`,
      {
        method: 'POST',
        body: formData
      });
  },

  removeImage: async (adventureId: string, itemId: string, imageIndex: number) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/image/${imageIndex}`,
      {
        method: 'DELETE'
      });
  },

  getImageSeed: () => (Math.floor((new Date()).getTime() / 1000000) * 1000),

  getImageUrl: (file: string, imageSeed: number) => {
    const fileUuid = file.split('/').pop()?.split('.').shift()?.replace(/-/g, '');
    if (!fileUuid || fileUuid.length !== 32) {
      console.log("Invalid file UUID");
      return "";
    }

    const fileNumber = parseInt(fileUuid?.slice(25) || '', 16);
    return `${url_prefix}/files/${file}?token=${imageSeed ^ fileNumber}`;
  },

  link: async (adventureId: string, fromItemId: string, toItemId: string) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/links`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fromItemId, toItemId })
    });
  },

  unlink: async (adventureId: string, fromItemId: string, toItemId: string) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/unlink`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fromItemId, toItemId })
    });
  },

  setParent: async (adventureId: string, itemId: string, parentItemId: string) => {
    if (parentItemId) {
      return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/parent`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ parentId: parentItemId || null })
      });
    } else {
      return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/parent`,
      {
        method: 'DELETE'
      });  
    }
  },

  addMarker: async (adventureId: string, itemId: string, imageIndex: number, linkItemId: string|undefined, name: string, x: number, y: number, dot: MarkerDot) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/markers/${imageIndex}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ linkItemId, x, y, name, dot })
    });
  },

  removeMarker: async (adventureId: string, itemId: string, imageIndex: number, markerId: string) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/markers/${imageIndex}/${markerId}`,
    {
      method: 'DELETE'
    });
  },

  addAlias: async (adventureId: string, itemId: string, alias: string) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/aliases`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ alias })
      });
  },

  removeAlias: async (adventureId: string, itemId: string, index: number) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/aliases/${index}`,
      {
        method: 'DELETE'
      });
  },

  setIgnoreCanonicalName: async (adventureId: string, itemId: string, value: boolean) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/ignoreCanonicalName`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ignoreCanonicalName: value })
      });
  },
  
  setCaseSensitiveNames: async (adventureId: string, itemId: string, value: boolean) => {
    return await typedFetch<never>(`${url_prefix}/adventure/${adventureId}/items/${itemId}/caseSensitiveNames`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ caseSensitiveNames: value })
      });
  },

  fetchDataExport: async (adventureId: string) => {
    return await fetch(`${url_prefix}/adventure/${adventureId}/dataexport`);
  },

  getAdventureEventsUrl: (adventureId: string) => {
    return `${url_prefix}/adventure/${adventureId}/events`;
  },

  setSseSubscriberId: (subscriberId: number | undefined) => {
    sseSubscriberId = subscriberId;
  }
};

export default backend;